import { isDef } from '@lhb/func';
import { fixNumberSE } from '@/common/utils/ways';
import { getTimeNameByValue, typeEnum } from '@/views/surround/pages/index/ts-config';

// 点位测评类型选择项
export const SpotTypeOptions = [
  {
    value: 1,
    label: '街边店'
  },
  {
    value: 2,
    label: '商场店'
  },
];

// 点位测评类型选择项枚举
export const SpotTypeEnum = {
  Street: 1,
  Mall: 2
};

export const filteredCode = 'yht_all_milk_tea_000';

/** 点位测评半径选择项 */
export const radiusOptions = [
  { value: 500, label: '500m' },
  { value: 1000, label: '1km' },
  { value: 1500, label: '1.5km' },
  { value: 2000, label: '2km' },
  { value: 3000, label: '3km' },
  { value: 5000, label: '5km' },
  // 0108 新增10km半径
  { value: 10000, label: '10km' },
];

/** 获取半径名称 */
export function getRadiusNameByValue(value: number) {
  const item = radiusOptions.find((item: any) => item.value === value);
  if (item?.label) {
    return item.label;
  } else {
    return isDef(value) ? (+value < 1000 ? `${+value}m` : `${fixNumberSE(value / 1000.0)}km`) : '-';
  }
}

/** 点位测评查询方式选择项（文档不需要自定义形状） */
export const searchTypeOptions = [
  { label: '半径', value: typeEnum.radius, icon: 'asset-pc-dianweiceping_banjing' },
  { label: '步行', value: typeEnum.walk, icon: 'asset-pc-dianweiceping_buxing' },
  { label: '骑行', value: typeEnum.ride, icon: 'asset-pc-dianweiceping_qixing' },
  { label: '驾车', value: typeEnum.drive, icon: 'asset-pc-dianweiceping_jiache' },
];

/** 获取查询方式名称 */
export function getSearchTypeNameByValue(value: number) {
  const item = searchTypeOptions.find((item: any) => item.value === value);
  return item?.label || '';
}

/** 获取标题显示用查询方式名称（省略文案“半径”） */
export function getTitleSearchTypeNameByValue(value: number) {
  const item = searchTypeOptions.find((item: any) => item.value === value);
  return item?.label && item.label !== '半径' ? item.label : '';
}

/**
 * 范围模式枚举：步行/骑行/驾车下，「时间」与「距离」二选一的子模式
 * - time: 选项是分钟数，调等时圈接口时传 contours_minutes
 * - distance: 选项是米数，调等时圈接口时传 contours_meters
 * 半径模式（typeEnum.radius）下视为 distance，UI 上不展示 tab
 */
export const RangeModeEnum = {
  Time: 'time',
  Distance: 'distance',
} as const;
export type RangeMode = typeof RangeModeEnum[keyof typeof RangeModeEnum];

/** 时间 tab 的固定选项（前端写死，距离选项仍走 /spot/evaluation/configData 的 radiusTypes） */
export const TIME_OPTIONS = [
  { label: '5分钟', value: 5 },
  { label: '10分钟', value: 10 },
  { label: '15分钟', value: 15 },
  { label: '20分钟', value: 20 },
];
/** 首次进入「时间」tab 默认值 */
export const DEFAULT_TIME_VALUE = 10;
/** 首次进入「距离」tab 默认值（在 configData 返回的 radiusTypes 中按 value === 3000 命中） */
export const DEFAULT_DISTANCE_VALUE = 3000;

/**
 * @description 根据查询类型和半径值获取半径名称或时间名称
 * @param {number} searchType 查询类型 半径/步行/骑行/驾车
 * @param {RangeMode} rangeMode 步行/骑行/驾车下的子模式（time/distance），半径模式忽略
 * @param {number} radius 半径或距离值（米）
 * @param {number} minute 时间值（分钟）
 * @param {number} distance 步行/骑行/驾车的距离值（米）
 * @return {*}
 * @demo
    getRadiusNameBySearchTypeAndValue({ searchType: typeEnum.radius, radius: 1000 }); // 1km
    getRadiusNameBySearchTypeAndValue({ searchType: typeEnum.walk, minute: 20 }); // 20分钟
    getRadiusNameBySearchTypeAndValue({ searchType: typeEnum.walk, rangeMode: 'distance', distance: 3000 }); // 3km
 */
export function getRadiusNameBySearchTypeAndValue({ searchType, radius, minute, distance, rangeMode }: {searchType: number, radius?: any, minute?: any, distance?: any, rangeMode?: RangeMode}) {
  // 半径查询：按距离展示
  if (searchType === typeEnum.radius) {
    return getRadiusNameByValue(radius) || '';
  }
  // 步行/骑行/驾车 + 距离 tab：按距离展示
  if (rangeMode === RangeModeEnum.Distance) {
    return getRadiusNameByValue(distance) || '';
  }
  // 步行/骑行/驾车 + 时间 tab（默认）：按分钟展示
  return getTimeNameByValue(minute) || '';
}

/** 点位测评抽屉数据 */
export interface DrawerDataProps {
  /** 仅机会点周边信息入口启用：本品牌门店跳过数据权限，其余入口不传 */
  noDataPermission?: boolean;
  open: boolean;
  cityId: number|null;
  cityName: string;
  lng: string;
  lat: string;
  /** poi点位名称，兼容搜索地址时，高德返回的形成和服务端接口查询poi的名字不同时的字段 */
  poiName: string;
  /** 商圈id */
  modelClusterId: number|null;
  /** 商圈模型配置id */
  modelConfigId: number|null;
  /** 评测类型 */
  type;
  /** 查询类型，默认半径查询 1 */
  poiSearchType: number;
  /** 范围子模式：步行/骑行/驾车下区分时间/距离 tab；半径模式恒为 distance */
  rangeMode?: RangeMode;
  /** 查询范围：半径或时间 */
  radius: number|null;
  /** 步行/骑行/驾车 + 时间 tab 时的分钟数 */
  poiSearchMinute?: number|null;
  /** 步行/骑行/驾车 + 距离 tab 时的距离米数 */
  poiSearchDistance?: number|null;
  /** 绘制等时圈时的多边形路径 */
  points?: { lng: number, lat: number }[];
  /** 查询后返回的查询记录id */
  id: number|null;
  [key: string]: any;
}

/**
 * 根据查询类型转换为文档需要的距离值
 * @description 步行是500米分段，骑行1.5km、汽车3km的分段
 * @demo
    convertRadiusTextToDocValue({ poiSearchType: typeEnum.radius, radius: 1000 }); // 1000
    convertRadiusTextToDocValue({ poiSearchType: typeEnum.walk }); // 500
 */
export function convertRadiusToDocValue({ poiSearchType, radius }: { poiSearchType: number, radius: number|null }) {
  if (poiSearchType === typeEnum.radius) {
    return radius;
  } else if (poiSearchType === typeEnum.walk) {
    return 500;
  } else if (poiSearchType === typeEnum.ride) {
    return 1500;
  } else if (poiSearchType === typeEnum.drive) {
    return 3000;
  } else {
    return radius;
  }
}

/**
 * 根据查询类型转换为文档需要的距离文本
 * @description 步行是500米分段，骑行1.5km、汽车3km的分段
 * @demo
    convertRadiusTextToDocValue({ poiSearchType: typeEnum.radius, radius: 1000 }); // 1km
    convertRadiusTextToDocValue({ poiSearchType: typeEnum.walk }); // 500m
 */
export function convertRadiusTextToDocValue({ poiSearchType, radius }: { poiSearchType: number, radius: number|null }) {
  if (poiSearchType === typeEnum.radius) {
    return getRadiusNameByValue(radius || 0);
  } else if (poiSearchType === typeEnum.walk) {
    return '500m';
  } else if (poiSearchType === typeEnum.ride) {
    return '1.5km';
  } else if (poiSearchType === typeEnum.drive) {
    return '3km';
  } else {
    return getRadiusNameByValue(radius || 0);
  }
}


